/**
 * Re-grading after something grading depends on changed — the LEDGER, the gate, and the dispatch.
 *
 * WHAT A RE-GRADE IS MADE OF, and the split that shapes this file: the WAVE holds what the ANNOUNCEMENT needs, the
 * grading rows hold what the GRADING needs. A trigger hands its cause (and the refs, submissions and approval count
 * that describe it) to the dispatch as a `BatchIntent`, which records it on the run's batch alongside the rows it
 * creates; each row then resolves and records what it actually graded. When the last grading row of the exam goes
 * terminal, the wave's completion reads both off the batch and announces once — see `types/grading.job.ts`.
 *
 * THERE IS NO DRAINER ANY MORE, and that is the point of this shape. This file used to run a second scheduler
 * beside the job system: a claim on the exam with a stale-after lease, a heartbeat to refresh it, a poll loop
 * waiting for the exam to settle, a completeness re-check, and a per-batch wait for the rows it dispatched. Every
 * one of those has an equivalent in the queue, one altitude lower and already written:
 *
 *  - **Deferral** — `grading`'s `ready()` waits on `subtreeGradable` (this task's own solutions) and on the exam
 *    not settling (`settle` on its payload). A row enqueued mid-extraction simply sits there.
 *  - **Coalescing** — `grading`'s `dedupeKey` hashes the scope, and its handler resolves "every ready submission"
 *    at RUN time rather than at enqueue time. So five answer extractions enqueuing an unscoped row for task 3
 *    produce ONE row, which grades whoever is ready when it finally runs. That is the batch, without a batch.
 *  - **Once-per-exam** — `anyLiveJobForTypes(examId, ['grading'])` is false at exactly one moment per wave. The
 *    claim and the lease existed to manufacture that moment for the announcement; the queue answers it directly.
 *
 * THERE IS NO EXAM-LEVEL LEDGER ANY MORE EITHER, and the two objections that put one there are both answered by the
 * batch rather than by the exam:
 *
 *  - "finished rows are kept for a week, so the done rows span many past waves" — a `batchKey` says which wave, and
 *    the prune will not delete a row whose batch is still open.
 *  - "a row is not one trigger, since the second trigger to want the same work dedupes onto the first row" — still
 *    true, which is why an intent goes on the BATCH and not on a row. A dedupe cannot lose it, because a live row
 *    implies an open batch and there is exactly one per (exam, step).
 *
 * What that buys is not tidiness. An exam-level accumulator has to be DRAINED, and every bug this file has had was a
 * property of draining: two rows racing to take it, an entry left behind that no wave would ever answer for and that
 * then silenced later announcements, and submissions named as re-graded that no row had reached. A batch is one wave
 * by construction, so none of those states exist to be reached.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { readySubmissionIds } from '../../utils/exam-utils.js';
import { checkGradingSchemeComplete, checkMasterSolutionComplete } from '../../utils/solution-completeness.js';
import type { GradeTaskTarget } from '../agents/default-grading.js';
import type { BatchIntent } from '../jobs/batch-store.js';
import type { BatchOutcome } from '../jobs/step.js';
import type { GradingScope } from '../jobs/types/grading.job.js';

/**
 * Why a re-grade is happening. Named in the announcement, and for the two solution causes it also selects the
 * completeness check below.
 *
 * `manual_grading_insight` is here because every re-grade now goes through one dispatcher. It deliberately has no
 * completeness gate: it re-grades against a rubric the teacher just corrected by hand, on an exam that is already
 * graded, so a gap elsewhere in the scheme is not a reason to withhold it.
 */
export type RegradeCause =
  | 'new_submissions'
  | 'master_solution_update'
  | 'grading_scheme_update'
  | 'manual_grading_insight';

/** What a trigger knows about the re-grade it is asking for. */
export interface RegradeRequest {
  examId: string;
  causes: RegradeCause[];
  /** For `new_submissions`: only the new ones. For a manual insight: every submission BUT the corrected one. */
  submissionIds?: string[];
  /**
   * Main task refs the change actually touched. Omitted → every task.
   *
   * A solution run knows this and nothing downstream can re-derive it, so it has to travel with the cause: see
   * `changedTasks` on `SolutionProgress`.
   */
  taskRefs?: string[];
  /** `content` (default) / `languageUse`. A language-use-rule edit must not rewrite content grades, and vice versa. */
  target?: GradeTaskTarget;
  /**
   * Scopes a `languageUse` re-grade to ONE LanguageUseScheme — a task can belong to more than one, so without
   * this, re-grading a task also re-grades every OTHER scheme it happens to share that task with. Set
   * when the trigger already knows exactly which scheme changed (e.g. a chat-driven language-use refine
   * resolved to one scheme by group number/id) — see `GradingPayload.languageUseSchemeId`.
   */
  languageUseSchemeId?: string;
  /**
   * Batches a solo (1-member) LanguageUseScheme rule into this same 'content' row — see
   * `GradeTaskOptions.includeSoloLanguageUse`. Only set for `new_submissions`: a late-arriving submission's
   * first grade, not an edit-driven regrade, which must keep touching only the family that changed.
   */
  includeSoloLanguageUse?: boolean;
  /** Announcement detail — see `announceRegradeFinished`. Only meaningful for `manual_grading_insight`. */
  previouslyApprovedCount?: number;
}

/** What the announcement is built from, once the wave ends. */
export interface RegradeLedger {
  causes: RegradeCause[];
  submissionIds: string[];
  taskRefs: string[];
  previouslyApprovedCount?: number;
}

/** Whether a change of this kind may be graded against — an incomplete solution must not be. */
async function isGradeable(examId: string, cause: RegradeCause): Promise<boolean> {
  if (cause === 'master_solution_update') {
    const { missingTaskPaths } = await checkMasterSolutionComplete(examId);
    if (missingTaskPaths.length) {
      logger.info({ examId, missingTaskPaths }, 'Master solution incomplete — skipping re-grade');
      return false;
    }
  } else if (cause === 'grading_scheme_update') {
    const { missingTaskPaths } = await checkGradingSchemeComplete(examId);
    if (missingTaskPaths.length) {
      logger.info({ examId, missingTaskPaths }, 'Grading scheme incomplete — skipping re-grade');
      return false;
    }
  }
  return true;
}

async function allGradeable(examId: string, causes: RegradeCause[]): Promise<boolean> {
  for (const cause of causes) {
    if (!(await isGradeable(examId, cause))) return false;
  }
  return true;
}

async function readExamState(examId: string): Promise<ExamState | undefined> {
  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any, { projection: { examState: 1 } });
  return (exam as any)?.examState as ExamState | undefined;
}

/**
 * WHY this trigger is asking, in the shape the WAVE records it — see `BatchIntent`.
 *
 * ON THE ROWS, NOT ON THE EXAM, and that move is what retired the ledger this file used to keep. An exam-level
 * accumulator spans every wave the exam ever had, so it had to be drained — and a drain can race two rows against
 * each other, be left holding an entry no row will ever answer for, or announce submissions nothing graded. A row
 * belongs to exactly one wave by construction, and the wave's rows are found by its key.
 *
 * A TRIGGER WHOSE UNITS ALL DEDUPED still lands here, which is the case that used to need a document of its own: the
 * enqueue pushes this onto the row that absorbed the collision (see `JobDoc.intents`). Ten answer extractions each
 * naming their own submission now produce ten rows, so the dedupe case is rarer than it was — but two solution
 * changes hitting the same (task, submission) still collide, and the second cause must survive it.
 *
 * NO SCOPE HERE. It used to carry `taskRefs` and `submissionIds` because a grading row stood for a set and could not
 * say what it covered. A row names one submission and one main task now, so the scope is the payload and the outcome
 * is `result` — leaving this to carry only what neither can express.
 */
const intentOf = (request: RegradeRequest): BatchIntent => {
  // ONE `detail` OBJECT, built once. Two conditional spreads of the same key silently dropped whichever came first,
  // so a multi-cause request that also carried an approved count lost its causes.
  const detail: Record<string, unknown> = {
    ...(request.causes.length > 1 ? { causes: request.causes } : {}),
    ...(typeof request.previouslyApprovedCount === 'number'
      ? { previouslyApprovedCount: request.previouslyApprovedCount }
      : {}),
  };
  return {
    // One cause per intent. Several causes in one request means several asks, and the announcement unions them back.
    ...(request.causes.length === 1 ? { cause: request.causes[0] } : {}),
    ...(Object.keys(detail).length ? { detail } : {}),
  };
};

/** Every cause an intent carries — one, or the list a multi-cause request recorded. */
export const causesOfIntent = (intent: BatchIntent): RegradeCause[] => {
  const many = (intent.detail as { causes?: unknown } | undefined)?.causes;
  if (Array.isArray(many)) return many as RegradeCause[];
  return intent.cause ? [intent.cause as RegradeCause] : [];
};

/**
 * What the announcement is made of, read off the wave that just ended rather than off exam state.
 *
 * NO CLAIM, NO CLEAR, NO RACE. The right to announce is taken once, atomically, when the batch is closed
 * (`claimBatchCompletion`), so by the time this runs the caller has already won and this is a pure read. The
 * `findOneAndUpdate` dance the exam-level ledger needed — a filter that doubled as mutual exclusion, a blanket clear
 * whose atomicity had to be argued — is gone with the ledger.
 *
 * Empty causes still means "nothing to announce", which is how a FIRST grading stays silent: the grading tool
 * appends no intent, so the wave has none.
 */
export function ledgerFromBatch(batch: BatchOutcome): RegradeLedger {
  const causes = new Set<RegradeCause>();
  const taskRefs = new Set<string>();
  let previouslyApprovedCount: number | undefined;

  for (const intent of batch.intents) {
    for (const cause of causesOfIntent(intent)) causes.add(cause);
    const approved = (intent.detail as { previouslyApprovedCount?: unknown } | undefined)?.previouslyApprovedCount;
    if (typeof approved === 'number') previouslyApprovedCount = approved;
  }

  // WHAT WAS ACTUALLY GRADED, from the rows that SUCCEEDED — never what was asked for. Naming submissions that were
  // asked for but never reached is the announcement bug this whole shape exists to end, and it is now a distinction
  // the rows can make on their own: a failed or skipped row is simply not in this set.
  const graded = new Set<string>();
  for (const row of batch.succeeded) {
    for (const id of asStringList(row.result?.gradedSubmissionIds)) graded.add(id);
    for (const ref of asStringList(row.result?.gradedTaskRefs)) taskRefs.add(ref);
  }

  return {
    causes: [...causes],
    submissionIds: [...graded],
    taskRefs: [...taskRefs],
    ...(typeof previouslyApprovedCount === 'number' ? { previouslyApprovedCount } : {}),
  };
}

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * The grading scope this request asks for — every axis passed straight through, including `submissionIds`.
 *
 * The drainer deliberately DROPPED that one for solution causes, and was right to: what reached it was the UNION of
 * many triggers' ids, and a union is not a scope. One trigger's own list is. A manual insight names every submission
 * BUT the one the teacher just corrected by hand, and re-grading that one would immediately overwrite the edit that
 * started the chain — the exclusion survives now because it travels on this row instead of through a shared array.
 * The ledger's copy of the ids is announcement-only.
 *
 * `settle` on every row, because everything reaching this function is a BACKGROUND re-grade: nobody is holding a
 * turn open for it, and waiting is what makes one upload produce one grading run. See `GradingPayload.settle`.
 */
const gradingScopeFor = (request: RegradeRequest): GradingScope => {
  // EVERY CAUSE NAMES ITS SUBMISSIONS NOW, `new_submissions` included, and that is what the per-submission grading
  // unit bought. New arrivals used to ask for "whatever is queued" (`submissionScope: 'pendingRegrade'`) because ten
  // extractions passing ten singleton lists would have produced ten rows with ten dedupe keys — ten grading passes,
  // each with a consistency review comparing one grade with itself. The unit is now (task × submission), so ten
  // singleton lists produce exactly the ten rows they should, the review is a job of its own over the whole wave,
  // and nothing has to be resolved after the ask.
  return {
    settle: true,
    ...(request.taskRefs?.length ? { taskRefs: request.taskRefs } : {}),
    ...(request.target ? { target: request.target } : {}),
    ...(request.languageUseSchemeId ? { languageUseSchemeId: request.languageUseSchemeId } : {}),
    ...(request.includeSoloLanguageUse ? { includeSoloLanguageUse: true } : {}),
    ...(request.submissionIds?.length ? { submissionIds: request.submissionIds } : {}),
  };
};

// NO `pendingRegradeSubmissionIds` ANY MORE. It answered "whose answers should the running grading row grade?" —
// the late scope resolution that existed only because a grading row stood for a submission SET. A row names its
// submission in its payload now, so nothing has to be looked up when it runs, and there is no window between the
// look-up and an arrival for a submission to fall into.

/** The batch scope grading batches under — its own type name, since grading is not a `SetupStep`. */
const GRADING_BATCH_STEP = 'grading';

/** Whether this exam has a re-grade wave in flight — the signal a step reads to stay silent and let it announce. */
export async function regradeWaveInFlight(examId: string): Promise<boolean> {
  const { openBatchIntents } = await import('../jobs/batch-store.js');
  return (await openBatchIntents(examId, GRADING_BATCH_STEP)).length > 0;
}

/**
 * Refs some trigger has already queued a re-grade for and that has not been announced yet.
 *
 * Read FRESH rather than off a caller's exam snapshot, because the one caller is the grading-scheme step's closure
 * and the write it needs to see happens in the same transition — the refine row dispatches its own re-grade, then
 * the closure runs. A snapshot taken before that would miss it and the closure would dispatch a second, WIDER
 * re-grade over the same tasks: unscoped, and therefore including the submission a manual insight deliberately
 * left out.
 */
export async function queuedRegradeRefs(examId: string): Promise<string[]> {
  const { openBatchRows } = await import('../jobs/batch-store.js');
  const rows = await openBatchRows(examId, GRADING_BATCH_STEP);
  // READ OFF THE ROWS' PAYLOADS, because that is where a scope lives now. Only rows carrying a CAUSE count: a first
  // grading records no intent, and its tasks are not "already spoken for by a re-grade".
  const rawRefs = [
    ...new Set(
      rows
        .filter(row => (row.intents?.length ?? 0) > 0)
        .flatMap(row => {
          const payload = row.payload as { taskRefs?: unknown; taskNumber?: unknown };
          const refs = Array.isArray(payload.taskRefs) ? payload.taskRefs.map(String) : [];
          return refs.length ? refs : [String(payload.taskNumber ?? '')];
        })
        .filter(Boolean),
    ),
  ];
  if (rawRefs.length === 0) return [];

  // RESOLVED TO THE MAIN TASK EACH REF SITS UNDER, because the caller (`regradeWhatTheSchemeChanged`)
  // compares this against `mainTaskNumbersForIds`'s own main-task-granularity refs. A row's own scope can
  // be narrower than a main task (a chat-driven refine almost always names one subtask, e.g. "1.1"), and
  // returning that raw ref here made the comparison miss it every time — the guard's own intent ("a refine
  // already spoke for this, don't dispatch a second WIDER pass") silently never matched, so a redundant,
  // unscoped `target`-less regrade fired on top of the refine's own correctly-scoped one.
  const { groupTaskRefsByMainTask } = await import('../agents/gradingAgent.js');
  const groups = await groupTaskRefsByMainTask(examId, rawRefs);
  return [...new Set(groups.map(group => group.taskNumber))];
}

/**
 * Queues ONE background re-grade if — and only if — grading has already run for this exam and every change being
 * graded against is complete. Returns whether the work was accepted, which the caller needs: an accepted re-grade
 * announces its own result when it finishes, so the caller's own step announcement would be redundant. When it
 * does NOT accept, the caller announces instead — and that is how the teacher learns that an uploaded solution is
 * still incomplete.
 *
 * THE ONE DISPATCHER. Every re-grade goes through here: a re-uploaded solution, a refine's own intent, a manual
 * grading insight, newly extracted answers. That is what lets the announcement be reconciled in one place, and it
 * is why `target` and `previouslyApprovedCount` are parameters rather than a second path.
 *
 * THE INTENT IS RECORDED BY THE DISPATCH, not before it, and that removed a whole failure mode rather than merely
 * reordering two writes. The ask has to be readable before a `pendingRegrade`-scoped row runs, which is why it used
 * to be written first — and writing first meant a request that then queued NOTHING left an entry behind that no wave
 * would ever answer for. The engine records the intent on the batch only once the units are known to be non-empty
 * and alongside the rows themselves, so "wrote an ask, created no work" is no longer a reachable state. A request
 * whose rows all deduped is fine: a live row implies an open batch, and there is exactly one per (exam, step), so
 * the ask lands on the wave those rows belong to.
 *
 * Several causes can arrive together (a master solution and a grading scheme in one upload). They share one
 * re-grade rather than one each, but ALL of them must pass their completeness gate: if the grading scheme is fine
 * and the master solution isn't, grading anyway would silently swallow the gap the teacher needs to hear about.
 */
export async function dispatchRegradeIfGrading(request: RegradeRequest): Promise<boolean> {
  const { examId, causes } = request;
  if (!causes.length) return false;

  try {
    const state = await readExamState(examId);
    if (!state?.hasGradingStarted) {
      logger.info({ examId, causes }, 'Grading has not started — skipping re-grade');
      return false;
    }

    // Will anything be gradable at all? Accepting means the caller stays silent about its own step, so a re-grade
    // that can never grade anything must be declined HERE — otherwise the teacher hears nothing.
    //
    // A RUNNING EXTRACTION STILL COUNTS AS A YES, but no longer because the row resolves its own set: the set is
    // fixed when `batch.units` enumerates it. What makes it a yes is that the submissions still extracting will
    // dispatch their own `new_submissions` re-grade when they land, and — while this wave's rows are still live —
    // that dispatch JOINS this batch and is announced with it. So the work is not lost and the count stays at one.
    const solutionChanged = causes.some(cause => cause !== 'new_submissions');
    const requested = solutionChanged ? undefined : request.submissionIds;
    const readyNow = await readySubmissionIds(examId, requested);
    const answersExtracting = state.answerExtractionProgress?.status === StepState.IN_PROGRESS;
    if (!readyNow.length && !answersExtracting) {
      // Named ids with no answers of their own is the common cause: their extraction failed, so the caller's
      // announcement (which names the failed submissions) is the right outcome. Logged with the ids so the
      // difference between "extraction failed" and "we looked in the wrong place" is visible.
      logger.info(
        { examId, causes, requested, answerStatus: state.answerExtractionProgress?.status },
        'No gradable submission and no answer extraction running — skipping re-grade',
      );
      return false;
    }

    if (!(await allGradeable(examId, causes))) {
      logger.info({ examId, causes }, 'A changed solution is incomplete — skipping re-grade');
      return false;
    }

    // THE INTENT TRAVELS WITH THE DISPATCH, which is what removed the write-then-take-back this used to need. The
    // engine records it on the batch only once the units are known to be non-empty, and only alongside the rows — so
    // a request that names no gradable task writes nothing at all, rather than leaving an entry no wave will answer
    // for. And a request whose rows all deduped still lands on the batch those rows belong to, because a live row
    // means an open batch and there is exactly one per (exam, step).
    // THE WHOLE WAVE, not just the content pass — see `dispatchGradingWave`. The consistency and language-use
    // rows carry the same intent and join the same batch, so the announcement this request is accepting
    // responsibility for still fires exactly once, and only once every phase of it has finished.
    const { dispatchGradingWave } = await import('../jobs/dispatch-grading.js');
    const { jobIds, payloads } = await dispatchGradingWave(examId, gradingScopeFor(request), intentOf(request));
    // Judged on the ROWS rather than on the content payloads: accepting means the caller stays silent and this
    // wave announces for it, so anything queued at all — a language-use-only regrade included — is an accept.
    if (!jobIds.length) {
      logger.info({ examId, causes, taskRefs: request.taskRefs }, 'Re-grade named no gradable task — nothing queued');
      return false;
    }

    logger.info(
      {
        examId,
        causes,
        tasks: payloads.map(payload => payload.taskNumber),
        rows: jobIds.length,
        readyNow: readyNow.length,
      },
      'Queued background re-grade',
    );
    return true;
  } catch (err) {
    logger.warn({ err, examId, causes }, 'Failed to evaluate or queue re-grade');
    return false;
  }
}
